import React, { useMemo, useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Checkbox,
  ListItemText,
  OutlinedInput
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
import { reportingApi } from '../../services/reportingApi';

interface HistoricalComparisonProps {
  siteId: number;
  siteName?: string;
}

interface ComparisonData {
  site_id: number;
  site_name: string;
  years_compared: number[];
  yearly_data: {
    [year: number]: {
      monthly_data: {
        [month: number]: {
          month_name: string;
          kwh_generated: number | null;
          data_quality: string | null;
          has_data: boolean;
        };
      };
      total_kwh: number;
      months_with_data: number;
      average_monthly_kwh: number;
    };
  };
  monthly_comparison: {
    [month: number]: {
      month_name: string;
      values: { [year: string]: number | null };
      // Legacy fields (for backward compatibility)
      average: number;
      min: number;
      max: number;
      std_dev: number;
      // New historical statistics fields
      current_years_avg: number;
      current_years_min: number;
      current_years_max: number;
      long_term_average: number | null;
      q1_25th_percentile: number | null;
      q3_75th_percentile: number | null;
      historical_min: number | null;
      historical_max: number | null;
    };
  };
  statistics: {
    total_data_points: number;
    overall_average: number;
    overall_median: number;
    overall_min: number;
    overall_max: number;
    overall_std_dev: number;
  };
}

const HistoricalComparisonChart: React.FC<HistoricalComparisonProps> = ({
  siteId,
  siteName = 'Site'
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ComparisonData | null>(null);
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [showStatistics, setShowStatistics] = useState(false);
  const [chartType, setChartType] = useState<'monthly' | 'cumulative'>('monthly');

  // Load available years from the API
  useEffect(() => {
    const loadAvailableYears = async () => {
      try {
        const response = await reportingApi.getAvailableYears(siteId);
        const years = response.available_years || [];
        setAvailableYears(years);
        
        // Default to current year and previous year if available
        const currentYear = new Date().getFullYear();
        const defaultYears = years.filter(year => 
          year === currentYear || year === currentYear - 1
        ).slice(0, 2);
        
        // If no current/previous year data, use the two most recent years
        if (defaultYears.length === 0 && years.length > 0) {
          setSelectedYears(years.slice(0, 2));
        } else {
          setSelectedYears(defaultYears);
        }
      } catch (error) {
        console.error('Failed to load available years:', error);
        // Fallback to static years
        const currentYear = new Date().getFullYear();
        const fallbackYears = [currentYear, currentYear - 1, currentYear - 2];
        setAvailableYears(fallbackYears);
        setSelectedYears([currentYear, currentYear - 1]);
      }
    };
    
    loadAvailableYears();
  }, [siteId]);

  const fetchComparisonData = async () => {
    if (selectedYears.length === 0) return;
    
    // Allow selection of more years but warn about performance
    const limitedYears = selectedYears.slice(0, 10); // Increased limit to 10 years
    if (limitedYears.length !== selectedYears.length) {
      setSelectedYears(limitedYears);
    }

    setLoading(true);
    setError(null);

    try {
      // Use optimized endpoint for better performance
      const response = await reportingApi.getMultiYearComparison(siteId, limitedYears);
      console.log('Multi-year comparison response:', response);
      setData(response);
    } catch (err) {
      setError(`Failed to load comparison data: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedYears.length > 0) {
      fetchComparisonData();
    }
  }, [selectedYears, siteId]);

  const handleYearChange = (event: any) => {
    const value = event.target.value;
    setSelectedYears(typeof value === 'string' ? value.split(',').map(Number) : value);
  };

  const chartData = useMemo(() => {
    if (!data || !data.monthly_comparison) return [];

    const months = Object.keys(data.monthly_comparison).map(Number).sort((a, b) => a - b);
    const selectedYearsList = [...selectedYears];
    const cumulativeByYear: Record<number, Record<number, number | null>> = {};

    const currentDate = new Date();
    const calendarYear = currentDate.getFullYear();
    const calendarMonth = currentDate.getMonth() + 1;
    const MS_PER_DAY = 86_400_000;
    const daysInCalendarMonth = new Date(calendarYear, calendarMonth, 0).getDate();
    const monthStartMs = new Date(calendarYear, calendarMonth - 1, 1, 0, 0, 0, 0).getTime();
    const elapsedMs = Math.max(0, currentDate.getTime() - monthStartMs);
    const monthDurationMs = daysInCalendarMonth * MS_PER_DAY;
    const monthProgress =
      monthDurationMs > 0 ? Math.min(1, Math.max(0, elapsedMs / monthDurationMs)) : 1;
    const fractionalDaysElapsed = elapsedMs / MS_PER_DAY;

    const showCumulativePartial =
      selectedYearsList.includes(calendarYear) && monthProgress > 0 && monthProgress < 1;

    if (chartType === 'cumulative') {
      selectedYearsList.forEach(year => {
        let runningTotal = 0;
        cumulativeByYear[year] = {};
        months.forEach(month => {
          const value = data.monthly_comparison[month]?.values[year.toString()];
          if (value !== null && value !== undefined) {
            runningTotal += value;
          }
          if (year === calendarYear && month > calendarMonth) {
            cumulativeByYear[year][month] = null;
          } else {
            cumulativeByYear[year][month] = runningTotal > 0 ? runningTotal : 0;
          }
        });
      });
    }

    const runningCumulativeStats = {
      long_term_average: 0,
      q1_25th_percentile: 0,
      q3_75th_percentile: 0,
      historical_min: 0,
      historical_max: 0,
      current_years_avg: 0,
      current_years_min: 0,
      current_years_max: 0
    };

    const attachYearMeta = (pt: any, month: number) => {
      selectedYearsList.forEach(year => {
        const yearlyData = data.yearly_data?.[year];
        const monthlyData = yearlyData?.monthly_data?.[month];
        const dataSource = monthlyData && 'data_source' in monthlyData ? monthlyData.data_source : undefined;
        pt[`${year}_data_source`] = dataSource;
        pt[`${year}_data_quality`] = monthlyData && 'data_quality' in monthlyData ? monthlyData.data_quality : undefined;
        if (pt[`${year}_inProgress`] === undefined) {
          pt[`${year}_inProgress`] = null;
        }
      });
    };

    const prorateStat = (fullMonthVal: number | null | undefined, progress: number, cumulativeFull: number) => {
      const v = fullMonthVal || 0;
      return cumulativeFull - v + v * progress;
    };

    /** Resolve typical month, bounds, and quartiles for in-progress fan / pace math. */
    const resolveMonthlyExpectation = (md: ComparisonData['monthly_comparison'][1]) => {
      const withQ = (avg: number, minB: number, maxB: number, q1?: number | null, q3?: number | null) => {
        const lo = minB > 0 ? minB : avg * 0.65;
        const hi = maxB > 0 ? maxB : avg * 1.35;
        const qq1 = q1 != null && q1 > 0 ? q1 : lo;
        const qq3 = q3 != null && q3 > qq1 ? q3 : Math.max(hi, qq1 + avg * 0.05);
        return { avg, minBound: lo, maxBound: hi, q1: qq1, q3: qq3 };
      };

      const lt = md.long_term_average;
      if (lt != null && lt > 0) {
        return withQ(
          lt,
          md.historical_min != null && md.historical_min > 0 ? md.historical_min : lt * 0.65,
          md.historical_max != null && md.historical_max > 0 ? md.historical_max : lt * 1.35,
          md.q1_25th_percentile,
          md.q3_75th_percentile
        );
      }
      const q1 = md.q1_25th_percentile;
      const q3 = md.q3_75th_percentile;
      if (q1 != null && q3 != null && q3 > q1) {
        const mid = (q1 + q3) / 2;
        return withQ(mid, q1, q3, q1, q3);
      }
      const cy = md.current_years_avg;
      if (cy != null && cy > 0) {
        const cmin = md.current_years_min != null && md.current_years_min > 0 ? md.current_years_min : cy * 0.65;
        const cmax = md.current_years_max != null && md.current_years_max > 0 ? md.current_years_max : cy * 1.35;
        return withQ(cy, cmin, cmax, null, null);
      }
      const mn = md.historical_min;
      const mx = md.historical_max;
      if (mn != null && mx != null && mx > mn) {
        const mid = (mn + mx) / 2;
        return withQ(mid, mn, mx, null, null);
      }
      const legacyAvg = (md as { average?: number }).average;
      if (legacyAvg != null && legacyAvg > 0) {
        return withQ(legacyAvg, legacyAvg * 0.65, legacyAvg * 1.35, null, null);
      }
      return { avg: 0, minBound: 0, maxBound: 0, q1: 0, q3: 0 };
    };

    /** Monthly view: one point per month, no duplicate “May (13%)” row. */
    if (chartType === 'monthly') {
      const points: any[] = [];
      months.forEach(month => {
        const monthData = data.monthly_comparison[month];
        if (!monthData) return;

        const isCurrentMonthPartial =
          month === calendarMonth &&
          selectedYearsList.includes(calendarYear) &&
          monthProgress > 0 &&
          monthProgress < 1;

        const chartPoint: any = {
          month: monthData.month_name,
          monthNumber: month,
          plotX: month,
          monthLabel: monthData.month_name,
          isPartialMonth: false,
          isMonthlyInProgress: !!isCurrentMonthPartial,
          long_term_average: monthData.long_term_average,
          q1_25th_percentile: monthData.q1_25th_percentile,
          q3_75th_percentile: monthData.q3_75th_percentile,
          historical_min: monthData.historical_min,
          historical_max: monthData.historical_max,
          current_years_avg: monthData.current_years_avg,
          current_years_min: monthData.current_years_min,
          current_years_max: monthData.current_years_max
        };

        selectedYearsList.forEach(year => {
          const yearValue = monthData.values[year.toString()];
          chartPoint[`${year}`] = yearValue !== null && yearValue !== undefined ? yearValue : null;
          chartPoint[`${year}_fanLow`] = null;
          chartPoint[`${year}_fanExpected`] = null;
          chartPoint[`${year}_fanHigh`] = null;
          chartPoint[`${year}_fanActual`] = null;
        });
        attachYearMeta(chartPoint, month);
        points.push(chartPoint);
      });

      /** Fan from prior month’s actual to in-progress month: expected + band (narrows as the month advances). */
      const showMonthlyFan =
        selectedYearsList.includes(calendarYear) &&
        monthProgress > 0 &&
        monthProgress < 1 &&
        calendarMonth > 1;

      if (showMonthlyFan) {
        const iMay = points.findIndex((p) => p.monthNumber === calendarMonth);
        const iApr = points.findIndex((p) => p.monthNumber === calendarMonth - 1);
        if (iMay >= 0 && iApr >= 0) {
          const md = data.monthly_comparison[calendarMonth];
          const f = monthProgress;
          const { avg: histAvg, q1: histQ1, q3: histQ3, minBound, maxBound } = md
            ? resolveMonthlyExpectation(md)
            : { avg: 0, minBound: 0, maxBound: 0, q1: 0, q3: 0 };

          const aprVal = points[iApr][`${calendarYear}`];
          const mayPt = points[iMay];
          const actualMtd = mayPt[`${calendarYear}`];
          const anchorApr = aprVal !== null && aprVal !== undefined ? aprVal : null;

          // Benchmark “typical” energy for elapsed days only (for pace % vs average — not plotted as month-end).
          const typicalMtd = histAvg > 0 ? histAvg * f : 0;
          const paceRaw = typicalMtd > 1e-6 ? actualMtd / typicalMtd : 1;
          const paceForBand = Math.min(1.85, Math.max(0.2, paceRaw));

          // Month-end forecast: current monthly performance extrapolated (same axis as other full months).
          const projectedFullRaw = f > 1e-9 ? actualMtd / f : actualMtd;
          let projectedFull = projectedFullRaw;
          if (histAvg > 0) {
            projectedFull = Math.min(
              Math.max(projectedFullRaw, histAvg * 0.08),
              histAvg * 4
            );
          }

          const q1Full = histQ1 > 0 ? histQ1 : minBound;
          const q3Full = histQ3 > q1Full ? histQ3 : maxBound;
          const iqrFull = Math.max(q3Full - q1Full, histAvg * 0.08, 1);
          const remaining = 1 - f;
          const halfSpanFull = 0.5 * iqrFull * paceForBand * remaining;
          const lowFull = Math.max(0, projectedFull - halfSpanFull);
          const highFull = projectedFull + halfSpanFull;

          if (histAvg > 0 && anchorApr !== null && actualMtd !== null && actualMtd !== undefined) {
            mayPt[`${calendarYear}`] = null;
            mayPt.monthlyInProgressMeta = {
              histAvg,
              typicalMtd,
              paceRaw,
              pacePercentVsTypical: (paceRaw - 1) * 100,
              projectedFullMonth: projectedFull,
              projectedFullMonthRaw: projectedFullRaw,
              projectedClamped: projectedFull !== projectedFullRaw,
              iqrFull,
              halfSpanFull,
              fractionElapsed: f,
              remainingFraction: remaining
            };
            points[iApr][`${calendarYear}_fanLow`] = anchorApr;
            points[iMay][`${calendarYear}_fanLow`] = lowFull;
            points[iApr][`${calendarYear}_fanExpected`] = anchorApr;
            points[iMay][`${calendarYear}_fanExpected`] = projectedFull;
            points[iApr][`${calendarYear}_fanHigh`] = anchorApr;
            points[iMay][`${calendarYear}_fanHigh`] = highFull;
            points[iApr][`${calendarYear}_fanActual`] = anchorApr;
            points[iMay][`${calendarYear}_fanActual`] = actualMtd;
          }
        }
      }

      return points;
    }

    /** Cumulative: numeric X so the in-progress point sits between month-end ticks; dashed tail for the active year. */
    const chartPoints: any[] = [];

    const pushYearStartIfNeeded = (beforeMonth: number) => {
      if (beforeMonth !== 1 || !showCumulativePartial) return;
      const z: any = {
        plotX: 0,
        month: 'Year start',
        monthLabel: 'Year start (0 kWh)',
        monthNumber: 0,
        isPartialMonth: false,
        isInProgressTail: false,
        long_term_average: null,
        q1_25th_percentile: null,
        q3_75th_percentile: null,
        historical_min: null,
        historical_max: null,
        current_years_avg: null,
        current_years_min: null,
        current_years_max: null
      };
      selectedYearsList.forEach(year => {
        z[`${year}`] = 0;
        z[`${year}_inProgress`] = year === calendarYear ? 0 : null;
      });
      attachYearMeta(z, 1);
      chartPoints.push(z);
    };

    months.forEach(month => {
      const monthData = data.monthly_comparison[month];
      if (!monthData) return;

      runningCumulativeStats.long_term_average += monthData.long_term_average || 0;
      runningCumulativeStats.q1_25th_percentile += monthData.q1_25th_percentile || 0;
      runningCumulativeStats.q3_75th_percentile += monthData.q3_75th_percentile || 0;
      runningCumulativeStats.historical_min += monthData.historical_min || 0;
      runningCumulativeStats.historical_max += monthData.historical_max || 0;
      runningCumulativeStats.current_years_avg += monthData.current_years_avg || 0;
      runningCumulativeStats.current_years_min += monthData.current_years_min || 0;
      runningCumulativeStats.current_years_max += monthData.current_years_max || 0;

      const isPartialHere =
        showCumulativePartial && month === calendarMonth;

      if (isPartialHere) {
        pushYearStartIfNeeded(month);

        const plotXPartial = (month - 1) + monthProgress;
        const cumFull = runningCumulativeStats.long_term_average;
        const partialRow: any = {
          plotX: plotXPartial,
          month: `${monthData.month_name} (in progress)`,
          monthLabel: `${monthData.month_name} (${fractionalDaysElapsed.toFixed(2)} of ${daysInCalendarMonth} day-equivalents)`,
          monthNumber: plotXPartial,
          isPartialMonth: true,
          isInProgressTail: true,
          long_term_average: prorateStat(monthData.long_term_average, monthProgress, cumFull),
          q1_25th_percentile: prorateStat(monthData.q1_25th_percentile, monthProgress, runningCumulativeStats.q1_25th_percentile),
          q3_75th_percentile: prorateStat(monthData.q3_75th_percentile, monthProgress, runningCumulativeStats.q3_75th_percentile),
          historical_min: prorateStat(monthData.historical_min, monthProgress, runningCumulativeStats.historical_min),
          historical_max: prorateStat(monthData.historical_max, monthProgress, runningCumulativeStats.historical_max),
          current_years_avg: prorateStat(monthData.current_years_avg, monthProgress, runningCumulativeStats.current_years_avg),
          current_years_min: prorateStat(monthData.current_years_min, monthProgress, runningCumulativeStats.current_years_min),
          current_years_max: prorateStat(monthData.current_years_max, monthProgress, runningCumulativeStats.current_years_max)
        };

        selectedYearsList.forEach(year => {
          const cumulativeValue = cumulativeByYear[year]?.[month];
          if (year === calendarYear) {
            partialRow[`${year}`] = null;
            partialRow[`${year}_inProgress`] =
              cumulativeValue !== null && cumulativeValue !== undefined ? cumulativeValue : null;
          } else {
            // Keep a value here so the solid line does not break across the fractional x tick.
            // Same calendar progress through the month as today: linear between prior month-end and this month-end cumulative.
            const cumEnd =
              cumulativeValue !== null && cumulativeValue !== undefined ? cumulativeValue : null;
            const cumStart =
              month === 1 ? 0 : cumulativeByYear[year]?.[month - 1];
            if (cumEnd !== null && cumEnd !== undefined) {
              const start = cumStart !== null && cumStart !== undefined ? cumStart : month === 1 ? 0 : null;
              partialRow[`${year}`] =
                start !== null ? start + (cumEnd - start) * monthProgress : null;
            } else {
              partialRow[`${year}`] = null;
            }
            partialRow[`${year}_inProgress`] = null;
          }
        });
        attachYearMeta(partialRow, month);

        const prev = chartPoints[chartPoints.length - 1];
        if (prev && prev.plotX === month - 1) {
          prev[`${calendarYear}_inProgress`] = prev[`${calendarYear}`];
        }

        chartPoints.push(partialRow);

        const endRow: any = {
          plotX: month,
          month: monthData.month_name,
          monthLabel: monthData.month_name,
          monthNumber: month,
          isPartialMonth: false,
          isInProgressTail: false,
          long_term_average: runningCumulativeStats.long_term_average,
          q1_25th_percentile: runningCumulativeStats.q1_25th_percentile,
          q3_75th_percentile: runningCumulativeStats.q3_75th_percentile,
          historical_min: runningCumulativeStats.historical_min,
          historical_max: runningCumulativeStats.historical_max,
          current_years_avg: runningCumulativeStats.current_years_avg,
          current_years_min: runningCumulativeStats.current_years_min,
          current_years_max: runningCumulativeStats.current_years_max
        };

        selectedYearsList.forEach(year => {
          const cumulativeValue = cumulativeByYear[year]?.[month];
          if (year === calendarYear) {
            endRow[`${year}`] = null;
          } else {
            endRow[`${year}`] = cumulativeValue;
          }
          endRow[`${year}_inProgress`] = null;
        });
        attachYearMeta(endRow, month);
        chartPoints.push(endRow);
        return;
      }

      const chartPoint: any = {
        plotX: month,
        month: monthData.month_name,
        monthLabel: monthData.month_name,
        monthNumber: month,
        isPartialMonth: false,
        isInProgressTail: false,
        long_term_average: runningCumulativeStats.long_term_average,
        q1_25th_percentile: runningCumulativeStats.q1_25th_percentile,
        q3_75th_percentile: runningCumulativeStats.q3_75th_percentile,
        historical_min: runningCumulativeStats.historical_min,
        historical_max: runningCumulativeStats.historical_max,
        current_years_avg: runningCumulativeStats.current_years_avg,
        current_years_min: runningCumulativeStats.current_years_min,
        current_years_max: runningCumulativeStats.current_years_max
      };

      selectedYearsList.forEach(year => {
        chartPoint[`${year}`] = cumulativeByYear[year]?.[month] ?? null;
        chartPoint[`${year}_inProgress`] = null;
      });
      attachYearMeta(chartPoint, month);
      chartPoints.push(chartPoint);
    });

    chartPoints.sort((a, b) => a.plotX - b.plotX);
    return chartPoints;
  }, [data, selectedYears, chartType]);

  const getYearColor = (year: number, index: number) => {
    const colors = ['#2196F3', '#4CAF50', '#FF9800', '#F44336', '#9C27B0', '#00BCD4', '#795548'];
    return colors[index % colors.length];
  };

  const yearChange = useMemo(() => {
    if (!data || selectedYears.length < 2) return null;

    const sortedYears = [...selectedYears].sort((a, b) => b - a);
    const currentComparisonYear = sortedYears[0];
    const previousComparisonYear = sortedYears[1];
    
    // For year-to-date comparison, only sum up to current month if comparing current year
    const todayDate = new Date();
    const todayYear = todayDate.getFullYear();
    const todayMonth = todayDate.getMonth() + 1;
    
    const isComparingCurrentYear = currentComparisonYear === todayYear;
    const maxMonth = isComparingCurrentYear ? todayMonth : 12;
    
    // Calculate year-to-date totals for each year
    let currentYearToDate = 0;
    let previousYearToDate = 0;
    let averageYearToDate = 0;
    
    const ytdByYear: Record<number, number> = {};
    selectedYears.forEach((y) => {
      ytdByYear[y] = 0;
    });

    for (let month = 1; month <= maxMonth; month++) {
      const currentMonthData = data.monthly_comparison[month];
      if (!currentMonthData) continue;

      const cv = currentMonthData.values[currentComparisonYear.toString()];
      const pv = currentMonthData.values[previousComparisonYear.toString()];
      currentYearToDate += cv != null && cv !== undefined ? cv : 0;
      previousYearToDate += pv != null && pv !== undefined ? pv : 0;

      selectedYears.forEach((year) => {
        const yv = currentMonthData.values[year.toString()];
        if (yv != null && yv !== undefined) {
          ytdByYear[year] += yv;
        }
      });
    }

    const ytdTotals = selectedYears.map((y) => ytdByYear[y] ?? 0);
    averageYearToDate =
      ytdTotals.length > 0 ? ytdTotals.reduce((a, b) => a + b, 0) / ytdTotals.length : 0;

    if (previousYearToDate === 0 && averageYearToDate === 0) return null;

    const changePercent = previousYearToDate > 0 ? ((currentYearToDate - previousYearToDate) / previousYearToDate) * 100 : null;
    const changeValue = currentYearToDate - previousYearToDate;
    
    const avgChangePercent = averageYearToDate > 0 ? ((currentYearToDate - averageYearToDate) / averageYearToDate) * 100 : null;
    const avgChangeValue = currentYearToDate - averageYearToDate;

    return {
      currentYear: currentComparisonYear,
      previousYear: previousComparisonYear,
      currentYearToDate,
      previousYearToDate,
      averageYearToDate,
      changePercent,
      changeValue,
      avgChangePercent,
      avgChangeValue,
      isYearToDate: isComparingCurrentYear,
      maxMonth,
      isIncrease: changePercent !== null ? changePercent > 0 : false,
      isDecrease: changePercent !== null ? changePercent < 0 : false,
      isAboveAverage: avgChangePercent !== null ? avgChangePercent > 0 : false,
      isBelowAverage: avgChangePercent !== null ? avgChangePercent < 0 : false
    };
  }, [data, selectedYears]);

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading historical comparison data...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title={
          <Typography variant="h6">
            Historical Comparison - {siteName}
          </Typography>
        }
        subheader="Compare years with available data (up to 10 years supported)"
      />
      <CardContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Controls */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Select Years to Compare (Max 10)</InputLabel>
              <Select
                multiple
                value={selectedYears}
                onChange={handleYearChange}
                input={<OutlinedInput label="Select Years to Compare (Max 10)" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((year) => (
                      <Chip key={year} label={year} size="small" />
                    ))}
                  </Box>
                )}
              >
                {availableYears.map((year) => (
                  <MenuItem 
                    key={year} 
                    value={year}
                    disabled={selectedYears.length >= 10 && selectedYears.indexOf(year) === -1}
                  >
                    <Checkbox checked={selectedYears.indexOf(year) > -1} />
                    <ListItemText primary={year} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Chart Type</InputLabel>
              <Select
                value={chartType}
                onChange={(e) => setChartType(e.target.value as 'monthly' | 'cumulative')}
                label="Chart Type"
              >
                <MenuItem value="monthly">Monthly Values</MenuItem>
                <MenuItem value="cumulative">Cumulative (Year-to-Date)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              onClick={() => setShowStatistics(!showStatistics)}
              fullWidth
              sx={{ height: '100%' }}
            >
              {showStatistics ? 'Hide' : 'Show'} Statistics
            </Button>
          </Grid>
        </Grid>

        {/* Year-to-Date Comparison Summary */}
        {yearChange && (
          <Box sx={{ mb: 2 }}>
            {yearChange.changePercent !== null && (
              <Alert
                severity={yearChange.isIncrease ? 'success' : yearChange.isDecrease ? 'warning' : 'info'}
                icon={
                  yearChange.isIncrease ? <TrendingUp /> :
                  yearChange.isDecrease ? <TrendingDown /> :
                  <TrendingFlat />
                }
                sx={{ mb: 1 }}
              >
                <Typography variant="body2">
                  <strong>{yearChange.currentYear} vs {yearChange.previousYear}</strong>
                  {yearChange.isYearToDate && (
                    <span>{` (Year-to-Date through ${new Date(2000, yearChange.maxMonth - 1, 1).toLocaleString('default', { month: 'long' })})`}</span>
                  )}:{' '}
                  {yearChange.changePercent >= 0 ? '+' : ''}{yearChange.changePercent.toFixed(1)}%{' '}
                  ({yearChange.changeValue >= 0 ? '+' : ''}{yearChange.changeValue.toLocaleString()} kWh)
                </Typography>
              </Alert>
            )}
            
            {yearChange.avgChangePercent !== null && (
              <Alert
                severity={yearChange.isAboveAverage ? 'success' : yearChange.isBelowAverage ? 'warning' : 'info'}
                icon={
                  yearChange.isAboveAverage ? <TrendingUp /> :
                  yearChange.isBelowAverage ? <TrendingDown /> :
                  <TrendingFlat />
                }
              >
                <Typography variant="body2">
                  <strong>{yearChange.currentYear} vs Average</strong>
                  {yearChange.isYearToDate && (
                    <span> (Year-to-Date)</span>
                  )}:{' '}
                  {yearChange.avgChangePercent >= 0 ? '+' : ''}{yearChange.avgChangePercent.toFixed(1)}%{' '}
                  ({yearChange.avgChangeValue >= 0 ? '+' : ''}{yearChange.avgChangeValue.toLocaleString()} kWh)
                </Typography>
              </Alert>
            )}
          </Box>
        )}

        {/* Chart */}
        {chartData.length > 0 && (() => {
          const calendarYearNow = new Date().getFullYear();
          const showInProgressYearTail =
            chartType === 'cumulative' &&
            selectedYears.includes(calendarYearNow) &&
            chartData.some((p: any) => p.isPartialMonth);

          const showMonthlyInProgressFan =
            chartType === 'monthly' &&
            selectedYears.includes(calendarYearNow) &&
            chartData.some((p: any) => p[`${calendarYearNow}_fanExpected`] != null);

          const fanYearColorIndex =
            selectedYears.indexOf(calendarYearNow) >= 0
              ? selectedYears.indexOf(calendarYearNow)
              : 0;
          const fanYearStroke = getYearColor(calendarYearNow, fanYearColorIndex);

          return (
          <Box sx={{ height: 400, mb: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                {chartType === 'cumulative' ? (
                  <XAxis
                    type="number"
                    dataKey="plotX"
                    domain={[0, 12.5]}
                    ticks={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]}
                    tickFormatter={(x) =>
                      x >= 1 && x <= 12
                        ? new Date(2000, Math.round(x as number) - 1, 1).toLocaleString('default', {
                            month: 'short'
                          })
                        : ''
                    }
                    allowDecimals
                  />
                ) : (
                  <XAxis dataKey="month" />
                )}
                <YAxis 
                  tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                  label={{ value: chartType === 'cumulative' ? 'Cumulative kWh' : 'Monthly kWh', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip 
                  formatter={(value: number, name: string, props: any) => [
                    `${value?.toLocaleString() || 'N/A'} kWh`,
                    name === 'average' ? 'Historical Average' : 
                    name === 'min' ? 'Historical Minimum' :
                    name === 'max' ? 'Historical Maximum' : name
                  ]}
                  labelFormatter={(label, payload) =>
                    chartType === 'cumulative' && payload && payload[0]?.payload?.monthLabel
                      ? String(payload[0].payload.monthLabel)
                      : `Month: ${label}`
                  }
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const chartPoint = payload[0]?.payload as any;
                      const title =
                        chartType === 'cumulative' && chartPoint?.monthLabel
                          ? chartPoint.monthLabel
                          : label;
                      return (
                        <Box sx={{ 
                          backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                          border: '1px solid #ccc', 
                          borderRadius: 1, 
                          p: 2,
                          boxShadow: 2
                        }}>
                          <Typography variant="h6" sx={{ mb: 1 }}>
                            {title}
                          </Typography>
                          {chartPoint?.isPartialMonth && chartType === 'cumulative' && (
                            <Typography variant="body2" sx={{ mb: 1, fontStyle: 'italic', color: 'primary.main' }}>
                              In-progress month: cumulative kWh to date; dashed segment is the current calendar month.
                            </Typography>
                          )}
                          {chartPoint?.monthlyInProgressMeta && chartType === 'monthly' && (
                            <Typography variant="caption" display="block" sx={{ mb: 1, color: 'text.secondary' }}>
                              Run-rate month-end:{' '}
                              {Number(chartPoint.monthlyInProgressMeta.projectedFullMonthRaw).toLocaleString(undefined, {
                                maximumFractionDigits: 0
                              })}{' '}
                              kWh
                              {chartPoint.monthlyInProgressMeta.projectedClamped
                                ? ` (shown ${Number(chartPoint.monthlyInProgressMeta.projectedFullMonth).toLocaleString(undefined, { maximumFractionDigits: 0 })})`
                                : ''}
                              . vs avg for these days:{' '}
                              {chartPoint.monthlyInProgressMeta.pacePercentVsTypical >= 0 ? '+' : ''}
                              {Number(chartPoint.monthlyInProgressMeta.pacePercentVsTypical).toFixed(0)}%.
                            </Typography>
                          )}
                          {payload.map((entry: any, index: number) => {
                            const rawName = String(entry.name);
                            const inTail = rawName.endsWith('_inProgress');
                            const fanMatch = rawName.match(/^(\d+)_fan(Expected|Actual|Low|High)$/);
                            const yearKey = inTail ? rawName.replace('_inProgress', '') : fanMatch ? fanMatch[1] : rawName;
                            const value = entry.value;
                            const dataSource = chartPoint?.[`${yearKey}_data_source`];
                            const dataQuality = chartPoint?.[`${yearKey}_data_quality`];
                            const isPartialMonth = chartPoint?.isPartialMonth;
                            const fanPart = fanMatch?.[2];
                            const fanLabels: Record<string, string> = {
                              Low: 'FCST low',
                              Expected: 'FCST month',
                              High: 'FCST high',
                              Actual: 'MTD'
                            };
                            const displayName = fanPart
                              ? `${yearKey} — ${fanLabels[fanPart] || fanPart}`
                              : inTail
                                ? `${yearKey} (month-to-date)`
                                : yearKey;

                            return (
                              <Box key={index} sx={{ mb: 1 }}>
                                <Typography 
                                  sx={{ 
                                    color: entry.color,
                                    fontWeight: 'bold'
                                  }}
                                >
                                  {displayName}: {value?.toLocaleString() || 'N/A'} kWh
                                  {isPartialMonth && !inTail && !fanPart && (
                                    <span style={{ fontSize: '0.8em', fontWeight: 'normal' }}>
                                      {yearKey === String(calendarYearNow) ? ' (actual)' : ' (pro-rated)'}
                                    </span>
                                  )}
                                </Typography>
                                {dataSource && (
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      color: dataSource === 'scada' ? 'success.main' : 'info.main',
                                      fontWeight: 'bold',
                                      display: 'block'
                                    }}
                                  >
                                    📊 {dataSource === 'scada' ? 'Real SCADA Data' : 'Historical Reference'}
                                  </Typography>
                                )}
                                {dataQuality && dataSource === 'historical' && (
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      color: 'text.secondary',
                                      fontSize: '0.7rem',
                                      display: 'block'
                                    }}
                                  >
                                    Quality: {dataQuality}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
                
                {/* Long-term historical statistics overlay */}
                <Line
                  type="monotone"
                  dataKey="long_term_average"
                  stroke="#FF9800"
                  strokeWidth={3}
                  name="Long-term Average"
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="q1_25th_percentile"
                  stroke="#FFC107"
                  strokeDasharray="3 3"
                  strokeWidth={2}
                  name="25th Percentile (Q1)"
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="q3_75th_percentile"
                  stroke="#FFC107"
                  strokeDasharray="3 3"
                  strokeWidth={2}
                  name="75th Percentile (Q3)"
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="historical_min"
                  stroke="#E0E0E0"
                  strokeDasharray="2 2"
                  strokeWidth={1}
                  name="Historical Min"
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="historical_max"
                  stroke="#E0E0E0"
                  strokeDasharray="2 2"
                  strokeWidth={1}
                  name="Historical Max"
                  dot={false}
                />
                
                {/* Year lines: solid actuals; dashed segment for calendar YTD in cumulative mode */}
                {selectedYears.map((year, index) => {
                  const col = getYearColor(year, index);
                  const tail = showInProgressYearTail && year === calendarYearNow;
                  return (
                    <React.Fragment key={year}>
                      <Line
                        type="monotone"
                        dataKey={year.toString()}
                        stroke={col}
                        strokeWidth={3}
                        name={year.toString()}
                        connectNulls={false}
                        dot={{ fill: col, strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6, stroke: col, strokeWidth: 2 }}
                      />
                      {tail && (
                        <Line
                          type="linear"
                          dataKey={`${year}_inProgress`}
                          stroke={col}
                          strokeWidth={3}
                          strokeDasharray="7 5"
                          name={`${year}_inProgress`}
                          legendType="none"
                          connectNulls={false}
                          dot={false}
                          isAnimationActive={false}
                        />
                      )}
                    </React.Fragment>
                  );
                })}

                {showMonthlyInProgressFan && (
                  <>
                    <Line
                      type="linear"
                      dataKey={`${calendarYearNow}_fanLow`}
                      name={`${calendarYearNow} range (low)`}
                      stroke="#78909C"
                      strokeWidth={2}
                      strokeDasharray="5 4"
                      dot={false}
                      connectNulls={false}
                      isAnimationActive={false}
                    />
                    <Line
                      type="linear"
                      dataKey={`${calendarYearNow}_fanExpected`}
                      name={`${calendarYearNow} forecast (month-end)`}
                      stroke="#00897B"
                      strokeWidth={2}
                      strokeDasharray="6 4"
                      dot={false}
                      connectNulls={false}
                      isAnimationActive={false}
                    />
                    <Line
                      type="linear"
                      dataKey={`${calendarYearNow}_fanHigh`}
                      name={`${calendarYearNow} range (high)`}
                      stroke="#78909C"
                      strokeWidth={2}
                      strokeDasharray="5 4"
                      dot={false}
                      connectNulls={false}
                      isAnimationActive={false}
                    />
                    <Line
                      type="linear"
                      dataKey={`${calendarYearNow}_fanActual`}
                      name={`${calendarYearNow} actual (MTD)`}
                      stroke={fanYearStroke}
                      strokeWidth={3}
                      strokeDasharray="8 4"
                      dot={false}
                      connectNulls={false}
                      isAnimationActive={false}
                    />
                  </>
                )}
              </LineChart>
            </ResponsiveContainer>
          </Box>
          );
        })()}

        {/* Statistics */}
        {showStatistics && data?.statistics && (
          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardHeader title="Statistical Summary" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Data Points</Typography>
                  <Typography variant="h6">{data.statistics.total_data_points}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Average</Typography>
                  <Typography variant="h6">{data.statistics.overall_average.toLocaleString()} kWh</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Minimum</Typography>
                  <Typography variant="h6">{data.statistics.overall_min.toLocaleString()} kWh</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Maximum</Typography>
                  <Typography variant="h6">{data.statistics.overall_max.toLocaleString()} kWh</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
      </CardContent>
    </Card>
  );
};

export default HistoricalComparisonChart;
